import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, RefreshCw, CheckCircle, XCircle,
  SkipForward, ChevronRight, Folder, Download, AlertCircle
} from "lucide-react";

// Turkish export header → English field key mapping (for auto-map from exported files)
const TR_HEADER_TO_KEY = {
  "soru kodu": "question_code",
  "soru metni": "question_text",
  "şık a": "option_a", "sik a": "option_a",
  "şık b": "option_b", "sik b": "option_b",
  "şık c": "option_c", "sik c": "option_c",
  "şık d": "option_d", "sik d": "option_d",
  "doğru cevap": "correct_answer", "dogru cevap": "correct_answer",
  "çözüm": "solution_text", "cozum": "solution_text",
  "arşiv": "archive_name", "arsiv": "archive_name",
  "ders": "lesson_name",
  "konu": "topic",
  "alt konu": "subtopic",
  "ünite": "unit_name", "unite": "unit_name",
  "zorluk": "difficulty_level",
  "sunum tipi": "display_type",
  "kaynak": "source_name",
  "sınav grubu": "exam_group", "sinav grubu": "exam_group",
  "taksonomi 1": "taxonomy_level_1",
  "taksonomi 2": "taxonomy_level_2",
  "taksonomi 3": "taxonomy_level_3",
  "etiketler": "tags",
  "durum": "status",
  "soru görseli url": "question_image_url", "soru gorseli url": "question_image_url",
  "soru video url": "question_video_url",
  "soru gif url": "question_gif_url",
  "animasyon video url": "animation_video_url",
  "kısa video url": "short_video_vertical_url", "kisa video url": "short_video_vertical_url",
  "açıklama video url": "explanation_video_horizontal_url", "aciklama video url": "explanation_video_horizontal_url",
  "açıklama görseli url": "explanation_image_url", "aciklama gorseli url": "explanation_image_url",
  "a şık görseli url": "option_a_image_url", "a sik gorseli url": "option_a_image_url",
  "b şık görseli url": "option_b_image_url", "b sik gorseli url": "option_b_image_url",
  "c şık görseli url": "option_c_image_url", "c sik gorseli url": "option_c_image_url",
  "d şık görseli url": "option_d_image_url", "d sik gorseli url": "option_d_image_url",
};

function autoMapColumns(columns, allFields) {
  const autoMap = {};
  // Strip BOM and normalize
  const normCol = (s) => s.replace(/^\ufeff/, "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const normKey = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  allFields.forEach(({ key }) => {
    // 1) Exact normalized match (for English-keyed files)
    const exactMatch = columns.find((c) => normCol(c) === normKey(key));
    if (exactMatch) { autoMap[key] = exactMatch; return; }
    // 2) Turkish header lookup (for files exported from this platform)
    const trMatch = columns.find((c) => TR_HEADER_TO_KEY[c.replace(/^\ufeff/, "").toLowerCase().trim()] === key);
    if (trMatch) { autoMap[key] = trMatch; }
  });
  return autoMap;
}

const REQUIRED_FIELDS = [
  { key: "question_code", label: "Soru Kodu *" },
  { key: "question_text", label: "Soru Metni *" },
  { key: "option_a", label: "Seçenek A *" },
  { key: "option_b", label: "Seçenek B *" },
  { key: "option_c", label: "Seçenek C *" },
  { key: "option_d", label: "Seçenek D *" },
  { key: "correct_answer", label: "Doğru Cevap *" }
];
const OPTIONAL_FIELDS = [
  { key: "archive_name", label: "Arşiv Adı" },
  { key: "lesson_name", label: "Ders Adı" },
  { key: "topic", label: "Konu" },
  { key: "subtopic", label: "Alt Konu" },
  { key: "unit_name", label: "Ünite Adı" },
  { key: "solution_text", label: "Çözüm/Açıklama" },
  { key: "difficulty_level", label: "Zorluk (easy/medium/hard)" },
  { key: "display_type", label: "Sunum Tipi (T / R / TR / ZTR / V)" },
  { key: "question_type", label: "Soru Türü (multiple_choice — opsiyonel)" },
  { key: "source_name", label: "Kaynak Adı" },
  { key: "exam_group", label: "Sınav Grubu (YKS/KPSS...)" },
  { key: "taxonomy_level_1", label: "Taksonomi 1" },
  { key: "taxonomy_level_2", label: "Taksonomi 2" },
  { key: "taxonomy_level_3", label: "Taksonomi 3" },
  { key: "tags", label: "Etiketler (virgülle ayrılmış)" },
  { key: "status", label: "Durum (active/inactive)" },
  { key: "question_image_url", label: "Soru Görseli URL" },
  { key: "question_video_url", label: "Soru Video URL" },
  { key: "question_gif_url", label: "Soru GIF URL" },
  { key: "animation_video_url", label: "Animasyon Video URL" },
  { key: "short_video_vertical_url", label: "Kısa Video URL" },
  { key: "explanation_video_horizontal_url", label: "Açıklama Video URL" },
  { key: "explanation_image_url", label: "Açıklama Görseli URL" },
  { key: "option_a_image_url", label: "A Şık Görseli URL" },
  { key: "option_b_image_url", label: "B Şık Görseli URL" },
  { key: "option_c_image_url", label: "C Şık Görseli URL" },
  { key: "option_d_image_url", label: "D Şık Görseli URL" },
];

export default function BulkImport() {
  const [tab, setTab] = useState("file");
  const [step, setStep] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateHandling, setDuplicateHandling] = useState("skip");
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveFiles, setDriveFiles] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(false);

  // Image import state
  const [imgPreview, setImgPreview] = useState(null);
  const [imgSession, setImgSession] = useState(null);
  const [imgImporting, setImgImporting] = useState(false);
  const [imgProgress, setImgProgress] = useState(null);
  const [imgFolderName, setImgFolderName] = useState("Image-01");

  useEffect(() => {
    if (tab === "drive") {
      api.get("/drive/status")
        .then((r) => setDriveStatus(r.data))
        .catch(() => setDriveStatus({ configured: false, message: "Drive durumu alınamadı" }));
    }
  }, [tab]);

  const scanImageFolder = async () => {
    setImgPreview(null); setImgSession(null); setImgProgress(null);
    setLoadingDrive(true);
    try {
      const r = await api.post(`/import/images/preview?folder_name=${encodeURIComponent(imgFolderName)}`);
      setImgPreview(r.data);
      setImgSession(r.data.session_id);
      toast.success(`Tarama tamamlandı: ${r.data.matched} eşleşme`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klasör taranamadı");
    } finally {
      setLoadingDrive(false);
    }
  };

  const startImageImport = async () => {
    if (!imgSession) return;
    setImgImporting(true);
    try {
      await api.post(`/import/images/confirm/${imgSession}`);
      toast.success("Import başlatıldı, arka planda devam ediyor...");
      // Poll progress
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/import/images/progress/${imgSession}`);
          setImgProgress(r.data);
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(poll);
            setImgImporting(false);
            if (r.data.status === "completed") toast.success(`Import tamamlandı: ${r.data.success}/${r.data.total} başarılı`);
          }
        } catch { clearInterval(poll); setImgImporting(false); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import başlatılamadı");
      setImgImporting(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      // Auto-map exact column matches
      const allFields = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
      setMapping(autoMapColumns(res.data.columns, allFields));
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dosya yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const loadDriveFiles = async () => {
    setLoadingDrive(true);
    try {
      const res = await api.get("/drive/files");
      setDriveFiles(res.data.files);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drive dosyaları yüklenemedi");
    } finally {
      setLoadingDrive(false);
    }
  };

  const handleDriveSelect = async (fileId) => {
    setUploading(true);
    try {
      const res = await api.get(`/drive/download/${fileId}`);
      setPreview(res.data);
      const allFields = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
      setMapping(autoMapColumns(res.data.columns, allFields));
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drive dosyası indirilemedi");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    const missingRequired = REQUIRED_FIELDS.filter(({ key }) => !mapping[key]);
    if (missingRequired.length > 0) {
      toast.error(`Zorunlu alanlar eşleştirilmedi: ${missingRequired.map((f) => f.label).join(", ")}`);
      return;
    }
    setImporting(true);
    try {
      const res = await api.post(`/import/confirm/${preview.session_id}`, {
        column_mapping: mapping,
        duplicate_handling: duplicateHandling
      });
      setResults(res.data);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import başarısız");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setPreview(null);
    setMapping({});
    setResults(null);
    setDuplicateHandling("skip");
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("qbank_token");
    const { BACKEND_URL_BASE } = require("../services/api");
    const url = `${BACKEND_URL_BASE}/api/import/template`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "soru_sablonu.csv";
        a.click();
      });
  };

  return (
    <div className="p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Toplu Import
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Excel veya CSV dosyasından soru içe aktar</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadTemplate}
          data-testid="download-template-btn"
          className="h-8 text-xs gap-1.5 border-slate-200"
        >
          <Download size={13} /> Şablon İndir
        </Button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6 text-xs">
        {["Dosya Yükle", "Sütun Eşleştir", "Sonuçlar"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-medium text-xs ${
              step > i + 1 ? "bg-green-500 text-white" : step === i + 1 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"
            }`}>{step > i + 1 ? "✓" : i + 1}</div>
            <span className={step === i + 1 ? "font-medium text-slate-800" : "text-slate-400"}>{s}</span>
            {i < 2 && <ChevronRight size={12} className="text-slate-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="max-w-xl">
          {/* Tabs */}
          <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setTab("file")}
              data-testid="tab-file"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "file" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
            >
              Dosya Yükle
            </button>
            <button
              onClick={() => setTab("drive")}
              data-testid="tab-drive"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "drive" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
            >
              Google Drive
            </button>
            <button
              onClick={() => setTab("images")}
              data-testid="tab-images"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "images" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
            >
              Görsel Import
            </button>
          </div>

          {tab === "file" && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFileUpload(e.dataTransfer.files[0]);
              }}
              data-testid="import-drop-zone"
              className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
                dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <FileSpreadsheet size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-1">Excel veya CSV dosyasını sürükleyin</p>
              <p className="text-xs text-slate-400 mb-4">.xlsx, .xls, .csv desteklenir</p>
              <label>
                <Button variant="outline" size="sm" disabled={uploading} data-testid="select-import-file-btn" asChild className="h-8 text-xs cursor-pointer">
                  <span>{uploading ? "Yükleniyor..." : "Dosya Seç"}</span>
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                />
              </label>
            </div>
          )}

          {tab === "drive" && (
            <div className="border border-slate-200 rounded-lg p-5">
              {!driveStatus ? (
                <p className="text-sm text-slate-400">Drive durumu yükleniyor...</p>
              ) : !driveStatus.configured ? (
                <div className="flex items-start gap-3">
                  <AlertCircle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Google Drive Yapılandırılmamış</p>
                    <p className="text-xs text-slate-500 mb-3">{driveStatus.message}</p>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 space-y-1">
                      <p className="font-medium text-slate-700">Yapılandırma için .env dosyasına ekleyin:</p>
                      <code className="block font-mono text-slate-600">GOOGLE_SERVICE_ACCOUNT_JSON='&#123;...&#125;'</code>
                      <code className="block font-mono text-slate-600">GOOGLE_DRIVE_FOLDER_ID='1abc...'</code>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Folder size={14} className="text-blue-500" />
                      <span className="text-sm font-medium text-slate-700">{driveStatus.folder_name}</span>
                      <span className="text-xs text-slate-400">klasörü bağlı</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadDriveFiles} disabled={loadingDrive} data-testid="refresh-drive-btn" className="h-7 text-xs gap-1">
                      <RefreshCw size={11} className={loadingDrive ? "animate-spin" : ""} /> Yenile
                    </Button>
                  </div>

                  {driveFiles.length === 0 ? (
                    <Button size="sm" onClick={loadDriveFiles} data-testid="load-drive-files-btn" className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs">
                      Dosyaları Listele
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      {driveFiles.map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-md hover:bg-slate-50 transition-colors"
                          data-testid={`drive-file-${f.id}`}>
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet size={14} className="text-green-500" />
                            <div>
                              <p className="text-xs font-medium text-slate-700">{f.name}</p>
                              <p className="text-xs text-slate-400">
                                {f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString("tr-TR") : ""}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleDriveSelect(f.id)}
                            disabled={uploading}
                            data-testid={`import-drive-file-${f.id}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs"
                          >
                            Seç
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Image Import Tab */}
          {tab === "images" && (
            <div className="space-y-4 max-w-2xl" data-testid="image-import-tab">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
                <p className="font-semibold mb-1">Görsel Import — Drive → R2</p>
                <ul className="space-y-0.5 list-disc list-inside text-blue-700">
                  <li>Drive'daki klasörü tarar, dosya adlarını parse eder</li>
                  <li>Soru numarasıyla DB'de eşleştirir</li>
                  <li>R tipi → question_image_url | TR tipi → option_x_image_url</li>
                  <li><code>++</code> suffix ignore edilir</li>
                </ul>
              </div>

              <div className="flex gap-2 items-center">
                <input
                  value={imgFolderName}
                  onChange={(e) => setImgFolderName(e.target.value)}
                  placeholder="Klasör adı (ör: Image-01)"
                  className="border border-slate-200 rounded px-3 h-8 text-xs flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="image-folder-input"
                />
                <Button
                  onClick={scanImageFolder}
                  disabled={loadingDrive}
                  data-testid="scan-image-folder-btn"
                  className="bg-slate-800 hover:bg-slate-700 text-white h-8 text-xs"
                >
                  {loadingDrive ? "Taranıyor..." : "Klasörü Tara (Dry-Run)"}
                </Button>
              </div>

              {imgPreview && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <p className="text-sm font-semibold text-slate-800">Tarama Sonucu — {imgPreview.folder_name}</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{imgPreview.matched}</p>
                        <p className="text-xs text-green-600 mt-1">Eşleşen</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-700">{imgPreview.unmatched}</p>
                        <p className="text-xs text-red-600 mt-1">Eşleşmeyen</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-slate-700">{imgPreview.total_files}</p>
                        <p className="text-xs text-slate-500 mt-1">Toplam Dosya</p>
                      </div>
                    </div>

                    {imgPreview.field_distribution && Object.keys(imgPreview.field_distribution).length > 0 && (
                      <div className="bg-slate-50 rounded p-3">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Alan Dağılımı:</p>
                        {Object.entries(imgPreview.field_distribution).map(([field, count]) => (
                          <div key={field} className="flex justify-between text-xs text-slate-600 py-0.5">
                            <span className="font-mono">{field}</span>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {imgPreview.unmatched_files?.length > 0 && (
                      <div className="bg-red-50 rounded p-3">
                        <p className="text-xs font-semibold text-red-700 mb-2">Eşleşmeyen Dosyalar (ilk 10):</p>
                        {imgPreview.unmatched_files.slice(0, 10).map((u, i) => (
                          <div key={i} className="text-xs text-red-600 py-0.5">
                            <span className="font-mono">{u.filename}</span> — {u.reason}
                          </div>
                        ))}
                      </div>
                    )}

                    {imgProgress && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <div className="flex justify-between text-xs text-blue-800 mb-2">
                          <span className="font-semibold">
                            {imgProgress.status === "completed" ? "✓ Tamamlandı" :
                             imgProgress.status === "failed" ? "✗ Hata" : "Yükleniyor..."}
                          </span>
                          <span>{imgProgress.done}/{imgProgress.total}</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all"
                            style={{ width: `${imgProgress.total > 0 ? (imgProgress.done / imgProgress.total) * 100 : 0}%` }}
                          />
                        </div>
                        {imgProgress.status === "completed" && (
                          <p className="text-xs text-blue-700 mt-2">
                            Başarılı: {imgProgress.success} | Hata: {imgProgress.errors?.length || 0}
                          </p>
                        )}
                      </div>
                    )}

                    {!imgImporting && imgProgress?.status !== "completed" && (
                      <Button
                        onClick={startImageImport}
                        data-testid="start-image-import-btn"
                        className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs w-full"
                      >
                        {imgPreview.matched} Görseli R2'ye Yükle ve DB'yi Güncelle
                      </Button>
                    )}
                    {imgProgress?.status === "completed" && (
                      <Button
                        onClick={() => { setImgPreview(null); setImgSession(null); setImgProgress(null); }}
                        className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs w-full"
                      >
                        Yeni Tarama
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Mapping */}
      {step === 2 && preview && (
        <div className="max-w-2xl fade-in">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={16} className="text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">{preview.filename}</p>
                  <p className="text-xs text-slate-500">{preview.total_rows} satır, {preview.columns.length} sütun</p>
                </div>
              </div>
              <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 underline">Değiştir</button>
            </div>
          </div>

          {/* Column mapping */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Sütun Eşleştirme</h3>
            <div className="grid grid-cols-2 gap-3">
              {[...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <div className="w-36 text-xs text-slate-600 shrink-0">{label}</div>
                  <select
                    value={mapping[key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [key]: e.target.value || undefined }))}
                    data-testid={`map-${key}`}
                    className="flex-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">— Eşleştirme yok —</option>
                    {preview.columns.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Duplicate handling */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Mükerrer question_code Davranışı</h3>
            <div className="space-y-2">
              {[
                { value: "skip", label: "Atla", desc: "Mevcut kayıtları değiştirme" },
                { value: "update", label: "Güncelle", desc: "Mevcut kaydın üzerine yaz" },
                { value: "error", label: "Hata Olarak İşaretle", desc: "Raporla ve geç" }
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-md hover:bg-slate-50 transition-colors"
                  data-testid={`duplicate-${opt.value}`}>
                  <input
                    type="radio"
                    name="duplicate"
                    value={opt.value}
                    checked={duplicateHandling === opt.value}
                    onChange={() => setDuplicateHandling(opt.value)}
                    className="text-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{opt.label}</p>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Preview table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Önizleme (İlk 5 Satır)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="bg-slate-50">
                  <tr>
                    {preview.columns.map((col) => (
                      <th key={col} className="px-3 py-2 text-left font-medium text-slate-500 truncate max-w-24">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={i} className={i < preview.preview.length - 1 ? "border-b border-slate-50" : ""}>
                      {preview.columns.map((col) => (
                        <td key={col} className="px-3 py-2 text-slate-600 truncate max-w-24">{row[col] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleConfirmImport}
              disabled={importing}
              data-testid="confirm-import-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-5 text-sm gap-2"
            >
              <Upload size={14} />
              {importing ? "Import Yapılıyor..." : `${preview.total_rows} Satırı İçe Aktar`}
            </Button>
            <Button variant="outline" onClick={reset} className="h-9 text-sm border-slate-200">
              İptal
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div className="max-w-md fade-in">
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-4">
            <h3 className="text-base font-semibold text-slate-800 mb-4">Import Sonuçları</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span className="text-sm font-medium text-green-700">Başarılı</span>
                </div>
                <span className="text-lg font-bold text-green-600">{results.success}</span>
              </div>
              {results.updated > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} className="text-blue-500" />
                    <span className="text-sm font-medium text-blue-700">Güncellendi</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600">{results.updated}</span>
                </div>
              )}
              {results.skipped > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2">
                    <SkipForward size={16} className="text-amber-500" />
                    <span className="text-sm font-medium text-amber-700">Atlandı (Mükerrer)</span>
                  </div>
                  <span className="text-lg font-bold text-amber-600">{results.skipped}</span>
                </div>
              )}
              {results.errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle size={16} className="text-red-500" />
                    <span className="text-sm font-medium text-red-700">Hatalar ({results.errors.length})</span>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {results.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600">
                        Satır {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={reset} className="bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm">
              Yeni Import
            </Button>
            <Button variant="outline" onClick={() => window.location.href = "/questions"} className="h-9 text-sm border-slate-200">
              Soru Listesine Git
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
